import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import Alert from '../common/Alert';

interface PermissionRouteProps {
  children: ReactNode;
  permission?: string;
  anyPermissions?: string[];
  allPermissions?: string[];
  fallbackPath?: string;
  showError?: boolean;
}

/**
 * PermissionRoute - Protects routes based on user permissions
 * 
 * Usage:
 * <PermissionRoute permission="mission:create">
 *   <CreateMissionPage />
 * </PermissionRoute>
 * 
 * <PermissionRoute anyPermissions={['*.*', 'organization:read']}>
 *   <OrganizationPage />
 * </PermissionRoute>
 * 
 * <PermissionRoute allPermissions={['mission:create', 'mission:write']}>
 *   <AdvancedMissionPage />
 * </PermissionRoute>
 */
export default function PermissionRoute({
  children,
  permission,
  anyPermissions,
  allPermissions,
  fallbackPath = '/dashboard',
  showError = true,
}: PermissionRouteProps) {
  const { isAuthenticated, hasPermission, hasAnyPermission, hasAllPermissions } = useAuthStore();

  // Must be authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check single permission
  if (permission && !hasPermission(permission)) {
    if (showError) {
      return (
        <div className="p-8">
          <Alert type="error">
            <div>
              <h3 className="font-semibold mb-2">Access Denied</h3>
              <p>You don't have permission to access this page.</p>
              <p className="text-sm mt-2">Required permission: <code className="bg-red-100 dark:bg-red-900 px-1 py-0.5 rounded">{permission}</code></p>
            </div>
          </Alert>
        </div>
      );
    }
    return <Navigate to={fallbackPath} replace />;
  }

  // Check any of the permissions
  if (anyPermissions && !hasAnyPermission(anyPermissions)) {
    if (showError) {
      return (
        <div className="p-8">
          <Alert type="error">
            <div>
              <h3 className="font-semibold mb-2">Access Denied</h3>
              <p>You don't have permission to access this page.</p>
              <p className="text-sm mt-2">Required (any of): {anyPermissions.map(p => (
                <code key={p} className="bg-red-100 dark:bg-red-900 px-1 py-0.5 rounded mr-1">{p}</code>
              ))}</p>
            </div>
          </Alert>
        </div>
      );
    }
    return <Navigate to={fallbackPath} replace />;
  }

  // Check all permissions
  if (allPermissions && !hasAllPermissions(allPermissions)) {
    if (showError) {
      return (
        <div className="p-8">
          <Alert type="error">
            <div>
              <h3 className="font-semibold mb-2">Access Denied</h3>
              <p>You don't have all required permissions to access this page.</p>
              <p className="text-sm mt-2">Required (all of): {allPermissions.map(p => (
                <code key={p} className="bg-red-100 dark:bg-red-900 px-1 py-0.5 rounded mr-1">{p}</code>
              ))}</p>
            </div>
          </Alert>
        </div>
      );
    }
    return <Navigate to={fallbackPath} replace />;
  }

  // All checks passed
  return <>{children}</>;
}

// Made with Bob